import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_HOTEL } from 'constants/offer'
import { isLpcRate } from 'lib/offer/propertyTypes'
import { meetsDynamicLpcCancellationWindow, meetsStaticCancellationWindow } from 'lib/offer/cancellationPolicyUtils'
import type moment from 'moment'
import type { ConnectionCancellationPolicies } from '@luxuryescapes/lib-refunds'

/** Days after booking that RFZ takes the balance. Charge must land before free cancel ends. */
export const RESERVE_FOR_ZERO_PAYMENT_BUFFER_DAYS = 4

const RESERVE_FOR_ZERO_CANCELLATION_POLICY_DAYS: Partial<Record<App.CancellationPolicyType, number>> = {
  'prior-to-check-in-one-day': 1,
  'prior-to-check-in-two-days': 2,
  'prior-to-check-in-three-days': 3,
  'prior-to-check-in-seven-days': 7,
  'prior-to-check-in-fourteen-days': 14,
  'prior-to-check-in-twenty-one-days': 21,
  'prior-to-check-in-twenty-eight-days': 28,
  'prior-to-check-in-thirty-one-days': 31,
  'prior-to-check-in-forty-five-days': 45,
  'prior-to-check-in-sixty-days': 60,
  'prior-to-check-in-ninety-days': 90,
  'partner-policy-prior-to-check-in-twenty-one-days': 21,
  'flexible-cancellation-forty-five-days': 45,
  'flexible-cancellation-charter-twenty-one-days': 21,
  'flexible-cancellation-ten-days': 10,
}

function isNonRefundable(
  cancellationPolicyType?: App.CancellationPolicyType,
  dynamicCancellationPolicies?: ConnectionCancellationPolicies,
): boolean {
  return cancellationPolicyType === 'non-refundable' ||
    dynamicCancellationPolicies?.policies?.[0]?.nonRefundable === true
}

function usesDynamicLpcPolicies(
  offer: App.AnyOffer,
  ignoreDynamicCancellationPolicy: boolean,
): boolean {
  return offer.type === OFFER_TYPE_ALWAYS_ON &&
    !!offer.property?.useDynamicCancellationPolicies &&
    !ignoreDynamicCancellationPolicy
}

function policyDaysFor(
  cancellationPolicyType?: App.CancellationPolicyType,
): number | undefined {
  return cancellationPolicyType ?
    RESERVE_FOR_ZERO_CANCELLATION_POLICY_DAYS[cancellationPolicyType] :
    undefined
}

/**
 * Offer-page / search tag when the shopper has not picked dates yet.
 * Checkout always has check-in, so it never uses this.
 * LPC: show the hint if the rate is not NR and is either dynamic or a mapped static slug.
 * Flash: hide until there is a check-in (cannot prove the window).
 */
export function isUndatedLpcDisplayCandidate(
  offer: App.AnyOffer,
  cancellationPolicyType?: App.CancellationPolicyType,
  ignoreDynamicCancellationPolicy = false,
  dynamicCancellationPolicies?: ConnectionCancellationPolicies,
): boolean {
  if (offer.type !== OFFER_TYPE_ALWAYS_ON || isNonRefundable(cancellationPolicyType, dynamicCancellationPolicies)) {
    return false
  }
  return usesDynamicLpcPolicies(offer, ignoreDynamicCancellationPolicy) ||
    policyDaysFor(cancellationPolicyType) !== undefined
}

/** PDP has no checkout available-rates payload. Same CM shape checkout already passes. */
function cancellationPoliciesFromPropertyRate(
  rate: App.EnrichedPropertyRate,
): ConnectionCancellationPolicies | undefined {
  const isHeadlineNonRefundable = rate.headlineCancellationPolicyType === 'nonRefundable'
  const deadlineIso = rate.fullyRefundableUntil ??
    rate.cancellationPolicies.find(policy => !policy.isFullyNonRefundable)?.endIsoDateTime
  if (!deadlineIso && !isHeadlineNonRefundable) {
    return undefined
  }
  return {
    description: [],
    policies: [{
      policyCode: 'dynamic',
      timezone: 'UTC',
      description: '',
      deadline: deadlineIso ? new Date(deadlineIso) : new Date(0),
      nonRefundable: isHeadlineNonRefundable,
    }],
  }
}

/**
 * Customer owns RFZ eligibility from the PO cancellation-policy slug (and CM rates when LPC is dynamic).
 * Dated static Flash/LPC: free cancel ends `policyDays` before check-in. Unmapped slugs (COM, hidden) fail.
 * Undated: false — use isUndatedLpcDisplayCandidate for offer-page / search hints.
 */
export function isPackageEligibleForZeroReservation(
  offer: App.AnyOffer,
  checkIn?: moment.Moment,
  cancellationPolicyType?: App.CancellationPolicyType,
  dynamicCancellationPolicies?: ConnectionCancellationPolicies,
  ignoreDynamicCancellationPolicy = false,
  /** Days from booking until the balance is charged. Group Payments passes its own (larger) buffer. */
  paymentBufferDays = RESERVE_FOR_ZERO_PAYMENT_BUFFER_DAYS,
): boolean {
  if (offer.type !== OFFER_TYPE_HOTEL && offer.type !== OFFER_TYPE_ALWAYS_ON) {
    return false
  }

  if (!checkIn || isNonRefundable(cancellationPolicyType, dynamicCancellationPolicies)) {
    return false
  }

  const policyDays = policyDaysFor(cancellationPolicyType)
  const useDynamicCancellationPolicies = usesDynamicLpcPolicies(offer, ignoreDynamicCancellationPolicy)

  if (useDynamicCancellationPolicies) {
    return meetsDynamicLpcCancellationWindow(paymentBufferDays, dynamicCancellationPolicies)
  }

  if (policyDays === undefined) {
    return false
  }

  return meetsStaticCancellationWindow(checkIn, policyDays, paymentBufferDays)
}

/**
 * LPC property-page (i) tag. Static: mapped prior-to-check-in slug + window.
 * Dynamic: free-cancel end on the rate (`fullyRefundableUntil`). If that date is missing, use the slug.
 */
export function isLpcPropertyRateEligibleForZeroReservation(
  offer: App.HotelOffer | undefined,
  rate: App.EnrichedPropertyRate,
  checkIn?: moment.Moment,
): boolean {
  if (!offer || offer.type !== OFFER_TYPE_ALWAYS_ON || !isLpcRate(rate)) {
    return false
  }
  const packageId = rate.service.packageId
  const pkg = offer.packages.find(item => item.id === packageId || item.lePackageId === packageId)
  if (!pkg) {
    return false
  }
  const ignoreDynamic = pkg.ignoreDynamicCancellationPolicy ?? false
  const cancellationPolicyType = pkg.roomRate?.cancellationPolicy?.type
  const cancellationPolicies = cancellationPoliciesFromPropertyRate(rate)
  if (!checkIn) {
    return isUndatedLpcDisplayCandidate(
      offer,
      cancellationPolicyType,
      ignoreDynamic,
      cancellationPolicies,
    )
  }
  const hasFreeCancelDeadline = !!(rate.fullyRefundableUntil ??
    rate.cancellationPolicies.find(policy => !policy.isFullyNonRefundable)?.endIsoDateTime)
  const noDeadlineOnRate = usesDynamicLpcPolicies(offer, ignoreDynamic) && !hasFreeCancelDeadline
  return isPackageEligibleForZeroReservation(
    offer,
    checkIn,
    cancellationPolicyType,
    cancellationPolicies,
    noDeadlineOnRate || ignoreDynamic,
  )
}
